import { useMemo } from "react";

import {
    AbfLoanExpanded,
    useLoanInfos,
    isLoanLiquidated,
    canLenderReclaimCollateral,
    useActiveWallet,
    isRefinanceEvent,
    isLoanBorrower,
    useIsLoanLender,
    LoanInfoSorting,
    getDerivedLoanAssetMints
} from "@bridgesplit/abf-react";
import { SortDirection } from "@bridgesplit/utils";
import { COPY } from "app/constants";

import { LoanRepaymentDetails } from "./type";
import { AlertConfig, getMarketHoursAlertConfig, isLoanUnknown } from "../common";

export function useLoanRepaymentDetails(loanInfo: AbfLoanExpanded | undefined) {
    const standard = useLoanRepaymentDetailsStandard(loanInfo);

    return standard;
}

function useLoanRepaymentDetailsStandard(loanInfo: AbfLoanExpanded | undefined): LoanRepaymentDetails | undefined {
    if (!loanInfo) return undefined;

    const totalPaid = loanInfo?.ledger.reduce((prev, curr) => prev + curr.principalRepaid, 0);
    const outstandingBalance = loanInfo?.ledger
        .filter((prev) => !prev.isFilled)
        .reduce((prev, curr) => prev + curr.ledgerDebt.total, 0);

    const totalClaimed = Math.max(0, totalPaid);

    return { outstandingBalance, totalClaimed };
}

export function useLoanFromParams(loanAddress: string | undefined): {
    loanExpanded: AbfLoanExpanded | undefined;
    isLoading: boolean;
    notFound: boolean;
} {
    const {
        data: loans,
        isLoading,
        isFetching
    } = useLoanInfos({
        loanFilter: {
            loanAddresses: loanAddress ? [loanAddress] : [],
            lenders: [],
            borrowers: [],
            principalMints: [],
            sortSide: SortDirection.Asc,
            collateralMints: [],
            sortType: LoanInfoSorting.StartTime
        },
        skip: !loanAddress
    });

    const loanExpanded = loans?.[0];
    return {
        loanExpanded,
        isLoading,
        notFound: !loanExpanded && !isLoading && !isFetching && !!loanAddress
    };
}
export function useRolesFromLoan(loanExpanded: AbfLoanExpanded | undefined) {
    const { activeWallet } = useActiveWallet();
    const userIsLender = useIsLoanLender(loanExpanded, activeWallet);
    const userIsBorrower = isLoanBorrower(loanExpanded, activeWallet);

    return { userIsLender, userIsBorrower };
}

export function useLoanCompleteData(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) {
        return { creditClaimed: false, debtClaimed: false };
    }

    // Check if all ledgers are fully repaid
    const creditClaimed = loanExpanded.ledger.every((ledger) => ledger.principalRepaid >= ledger.principalDue);

    // Check if borrower has claimed collateral
    const debtClaimed = loanExpanded.collateral.length === 0;

    return { creditClaimed, debtClaimed };
}

export function useDefaultedLoanDescription(loanExpanded: AbfLoanExpanded | undefined) {
    const wasLiquidated = isLoanLiquidated(loanExpanded);

    const canLenderReclaim = canLenderReclaimCollateral(loanExpanded);

    const { userIsLender, userIsBorrower } = useRolesFromLoan(loanExpanded);

    if (userIsBorrower) {
        // const liquidatedText = (() => {
        //     if (!data?.bidData.closed) return "Your collateral is being liquidated to repay the lender";
        //     if (data.percentLiquidated === 1) return "Your collateral has been liquidated to repay the loan";
        //     const collateralLiquidated = `${formatPercent(
        //         data.percentLiquidated
        //     )} of your collateral was liquidated to repay the lender`;
        //     if (!data.collateralRetained.length) return collateralLiquidated;
        //     return `${collateralLiquidated}. The remaining collateral was returned to your wallet`;
        // })();

        return `You defaulted on the loan`; // TODO: fix this to use events
    }

    if (userIsLender) {
        const defaultText = wasLiquidated
            ? "The borrower defaulted on the loan due insufficient collateral"
            : "The borrower failed to repay and has defaulted";

        if (!canLenderReclaim) {
            return `${defaultText}. Their outstanding debt has been returned to your ${COPY.STRATEGY_TERM.toLowerCase()}`;
        }

        return `${defaultText}. You can seize this asset at any time`;
    }

    return null;
}

export function useCompletedLoanDescription(loanExpanded: AbfLoanExpanded | undefined) {
    const { creditClaimed } = useLoanCompleteData(loanExpanded);

    const { userIsLender, userIsBorrower } = useRolesFromLoan(loanExpanded);

    const event = loanExpanded?.events[0];
    if (isLoanUnknown(loanExpanded)) {
        return "This loan contains an unrecognized or unsupported token. For more information or assistance, please open a support ticket in our Discord. Our team will be happy to help clarify the issue or provide further details.";
    }

    if (userIsBorrower) {
        if (isRefinanceEvent(event?.action)) return "Your loan has been refinanced";
        return `All payments have been completed. Your collateral has been returned to your wallet`;
    }
    if (userIsLender) {
        if (creditClaimed) {
            return `All repayments have been sent back to your ${COPY.STRATEGY_TERM.toLowerCase()}`;
        }
        return "You can now claim any remaining payments";
    }

    return null;
}

export function useLoanMarketHoursAlertConfig(loanExpanded: AbfLoanExpanded | undefined) {
    let isClosed: boolean | undefined;
    let alertConfig: AlertConfig | undefined;

    const allMints = getDerivedLoanAssetMints(loanExpanded);

    return useMemo(() => {
        if (!allMints) return { isClosed, alertConfig };
        return getMarketHoursAlertConfig(allMints);
    }, [allMints, isClosed, alertConfig]);
}
